import { getQueryParamsString } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import {
	getPartnerStatisticsFilterFormSchema,
	type PartnerStatisticsFilterFormModel,
} from "Partner/Modules/Statistics/Components/Forms/Schema/partnerStatisticsFilterFormSchema";
import { PartnerStatisticsPaths } from "Partner/Modules/Statistics/Routes/Routing";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Stack } from "@mui/material";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

type Props = {
	setIsFilterDrawerOpen: (isOpen: boolean) => void;
};

const filterResource = Resources.base.partner.statistics.filter;

export const PartnerStatisticsFilterForm: FC<Props> = ({
	setIsFilterDrawerOpen,
}) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const currentMarket = useMarket().market;
	const [searchParams] = useSearchParams();
	const searchParameters = Object.fromEntries(searchParams.entries());

	const form = useForm<PartnerStatisticsFilterFormModel>({
		resolver: zodResolver(getPartnerStatisticsFilterFormSchema()),
		defaultValues: {
			...searchParameters,
			regionId: searchParameters.regionId
				? Number(searchParameters.regionId)
				: null,
			marketId: searchParameters.marketId
				? Number(searchParameters.marketId)
				: currentMarket?.id
					? Number(currentMarket.id)
					: null,
		},
	});
	const selectedMarketId = form.watch("marketId");

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;

	const { data: markets } = useMarketListQuery();
	const { data: regions, refetch: refetchRegions } =
		useMarketRegionList(selectedMarketId);

	const handleSubmitFilter = () => {
		handleSubmit((data) => {
			const filteredData = Object.fromEntries(
				Object.entries(data).filter(
					([_, value]) => value !== null && value !== undefined,
				),
			);
			navigate(
				`${PartnerStatisticsPaths.Index.path}${getQueryParamsString({ ...filteredData })}`,
			);
			setIsFilterDrawerOpen(false);
		})();
	};

	const handleResetFilter = () => {
		form.reset({
			marketId: currentMarket?.id ?? null,
			regionId: null,
		});
		navigate(PartnerStatisticsPaths.Index.path);
	};

	useEffect(() => {
		selectedMarketId && refetchRegions();
	}, [selectedMarketId, refetchRegions]);

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="market-id"
						name="marketId"
						label={t(filterResource.market)}
						variant="outlined"
						control={control}
						errors={errors}
						options={(markets || []).map((market) => ({
							value: market.id,
							label: market.name,
						}))}
						fullWidth
						numeric
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>
				{selectedMarketId && (
					<Grid2 size={{ xs: 12 }}>
						<Select
							id="region-id"
							name="regionId"
							label={t(filterResource.region)}
							variant="outlined"
							control={control}
							errors={errors}
							options={(regions || []).map((region) => ({
								value: region.id,
								label: region.name,
							}))}
							fullWidth
							numeric
							sxLabel={{ mt: 0 }}
						/>
					</Grid2>
				)}
			</Grid2>
			<Stack mt={2} direction="row" spacing={2}>
				<Box>
					<Button variant="contained" onClick={handleSubmitFilter}>
						{t(filterResource.search)}
					</Button>
				</Box>
				<Box>
					<Button variant="outlined" onClick={handleResetFilter}>
						{t(filterResource.reset)}
					</Button>
				</Box>
			</Stack>
		</>
	);
};
